import { faLocationDot } from '@fortawesome/pro-light-svg-icons/faLocationDot';
import { faPaperPlane, faTrash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SingleSaleDetail } from '@spike/single-sale-model';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useMemo } from 'react';
import { getStatusLabel } from '../SaleDetailUtils';
import styles from './SaleDetailHeader.module.scss';

interface SaleDetailHeaderProps {
    sale: SingleSaleDetail;
    status?: string;
    saleDate?: string;
    sendingInvoice: boolean;
    showInvoiceActions: boolean;
    isIncomplete: boolean;
    onClose: () => void;
    onDelete: () => void;
    onSendInvoice: () => void;
}

export const SaleDetailHeader: FunctionComponent<SaleDetailHeaderProps> = ({
    sale,
    status,
    saleDate,
    sendingInvoice,
    showInvoiceActions,
    isIncomplete,
    onClose,
    onDelete,
    onSendInvoice
}) => {
    const { organizationMarketplaces, getOrganizationMarketplace } = useOrganizationMarketplaces();
    const marketplace = getOrganizationMarketplace(sale.marketplaceId);
    const marketplaceName = marketplace?.name || sale.marketplaceName;
    const showMarketplaceName = organizationMarketplaces.length > 1 && marketplaceName;
    const actionOptions = useMemo<Array<ThreeDotsDropdownOption>>(() => {
        const options: Array<ThreeDotsDropdownOption> = [];

        if (showInvoiceActions) {
            options.push({
                id: 'send-invoice',
                label: sendingInvoice ? 'Sending...' : 'Send Invoice',
                icon: faPaperPlane,
                disabled: sendingInvoice,
                onClick: onSendInvoice
            });
        }

        if (isIncomplete) {
            options.push({
                id: 'delete',
                label: 'Delete',
                icon: faTrash,
                onClick: onDelete
            });
        }

        return options;
    }, [isIncomplete, onDelete, onSendInvoice, sendingInvoice, showInvoiceActions]);

    return (
        <header className={styles.header}>
            <div>
                <span className={`${styles.status} ${styles[`status-${status}`] ?? ''}`}>
                    {status === 'completed' && '✓ '}
                    {getStatusLabel(status)}
                </span>
                <h2>Sale #{sale.saleNumber}</h2>
                <p>{saleDate}</p>
                {showMarketplaceName && (
                    <div className={styles.location}>
                        <FontAwesomeIcon
                            icon={faLocationDot}
                            className={styles.locationIcon}
                        />
                        <span>{marketplaceName}</span>
                    </div>
                )}
            </div>

            <div className={styles.headerActions}>
                <div className={styles.topActions}>
                    <ThreeDotsDropdown
                        options={actionOptions}
                        direction="down"
                        className={`${styles.actionsDropdown} ${isIncomplete ? styles.dangerDropdown : ''}`}
                    />

                    <button
                        type="button"
                        className={styles.mobileCloseButton}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default SaleDetailHeader;
